<template>
  <div class="input def-dropdown">
    <div class="input-wrapper">
      <select
        v-bind="inputAttrs"
        :id="inputId"
        :value="localValue"
        :aria-required="required ? 'true' : null"
        :aria-invalid="error ? 'true' : null"
        :aria-describedby="describedBy"
        class="select input-field"
        @change="onInput"
        @focus="isFocused = true"
        @blur="isFocused = false"
      >
        <option v-if="placeholder" disabled value="">{{ placeholder }}</option>
        <option v-for="option in sortedOptions" :key="option.value" :value="option.value">
          {{ option.label }}
        </option>
      </select>
      <label
        :id="labelId"
        :for="inputId"
        class="input-label input-label-control"
        :class="{ floating: isFocused || hasValue, error: !!error }"
      >
        {{ label }}<span v-if="required" aria-hidden="true"> *</span>
      </label>
    </div>
    <div v-if="error" :id="errorId" class="input-error" role="alert" aria-live="assertive">
      {{ error }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'Dropdown',
  inheritAttrs: false,
  props: {
    value: [String, Number],
    label: { type: String, required: true },
    options: { type: Array, required: true }, // [{ value, label }]
    placeholder: { type: String, default: '' },
    error: { type: String, default: '' },
    required: { type: Boolean, default: false },
    id: { type: String, default: null },
    sort: {
      type: [Boolean, String],
      default: 'asc', // true, 'asc', 'desc', or false
    },
  },
  data() {
    return {
      isFocused: false,
      localValue: this.normalizeValue(this.value),
    };
  },
  watch: {
    value(newVal) {
      this.localValue = this.normalizeValue(newVal);
    }
  },
  computed: {
    inputId() {
      return this.id || `dropdown-${this._uid}`;
    },
    labelId() {
      return `${this.inputId}-label`;
    },
    errorId() {
      return `${this.inputId}-error`;
    },
    describedBy() {
      const ids = [];
      if (this.$attrs['aria-describedby']) {
        ids.push(this.$attrs['aria-describedby']);
      }
      if (this.error) {
        ids.push(this.errorId);
      }
      return ids.join(' ') || null;
    },
    inputAttrs() {
      const attrs = { ...this.$attrs };
      delete attrs['aria-label'];
      delete attrs['aria-describedby'];
      delete attrs['aria-invalid'];
      delete attrs.autocomplete;
      return attrs;
    },
    hasValue() {
      return (
        this.localValue !== undefined &&
        this.localValue !== null &&
        this.localValue !== ''
      );
    },
    sortedOptions() {
      if (!this.sort || this.sort === false) return this.options;
      const sorted = [...this.options].sort((a, b) => {
        if (a.label < b.label) return -1;
        if (a.label > b.label) return 1;
        return 0;
      });
      if (this.sort === 'desc') return sorted.reverse();
      return sorted;
    }
  },
  methods: {
    normalizeValue(value) {
      return value === undefined || value === null ? '' : value;
    },
    onInput(event) {
      this.localValue = event.target.value;
      this.$emit('input', this.localValue);
    }
  },
};
</script>

<style scoped>
.select {
  height: 100%;
}
.input {
  border: 0;
  margin: 0;
  display: flex;
  padding: 0;
  position: relative;
  min-width: 0;
  flex-direction: column;
  vertical-align: top;
}
.input-label {
  text-align: left;
  pointer-events: none;
  transition: 0.2s cubic-bezier(0.4,0,0.2,1);
  background: transparent;
}
.input-label-control {
  top: 0;
  position: absolute;
  transform: translateY(24px) scale(1);
  z-index: 2;
}
.input-label-control.floating {
  transform: translateY(4px) scale(0.85);
  background: none;
}
.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}
.input-field {
  text-align: left;
  width: 100%;
  outline: none;
  transition: background-color 9999s ease-in-out 0s;
  padding: 24px 0 8px 0.75rem;
}
.input-field:-webkit-autofill,
.input-field:-webkit-autofill:focus,
.input-field:-webkit-autofill:hover,
.input-field:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 1000px transparent inset !important;
  box-shadow: 0 0 0 1000px transparent inset !important;
  transition: background-color 9999s ease-in-out 0s;
  background-color: transparent !important;
}
.input-field:-webkit-autofill ~ .input-label-control,
.input-field:autofill ~ .input-label-control {
  transform: translateY(4px) scale(0.85);
  background: none;
}
.input-error {
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.5em;
  text-align: left;
}
</style>
